import { CheckCircle2, MessageSquare, Phone, X, XCircle } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input, PhoneInput } from "../ui/input";
import React, { useCallback, useEffect, useState } from "react";
import { formatPhone, phoneHref } from "@servicepro/utils";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { cn } from "../../lib/utils";
import { getWindowJobsiteConfig } from "../../types/runtime-config";
import { submitLead } from "../../forms/submit-lead";

interface MobileStickyCallProps {
  /** Phone number for tap-to-call (required) */
  phone: string;
  /** Button text (default: "Call Now") */
  buttonText?: string;
  /** Enable pulsing animation for urgency */
  /** Threshold in pixels to scroll before showing button */
  scrollThreshold?: number;
  /** Allow user to dismiss the button */
  dismissible?: boolean;
  /** Session storage key for dismiss state */
  dismissKey?: string;
  /** Hide the Text Us button (show Call Now only) */
  showTextUs?: "yes" | "no";
  /** Bold: the call button in the accent, Text us outlined, so the bar reads on a dark band as well as a light one. */
  variant?: "standard" | "bold";
  /** Custom class name */
  className?: string;
}

/**
 * Mobile Sticky CTA Bar
 *
 * A fixed-position dual-action bar that appears on mobile after scrolling.
 * Features "Text Us" (opens lead form dialog) and "Call Now" (tel: link).
 */
const MobileStickyCall: React.FC<MobileStickyCallProps> = ({
  phone,
  buttonText = "Call Now",
  scrollThreshold = 400,
  dismissible = true,
  showTextUs = "yes",
  dismissKey = "sp_sticky_call_dismissed",
  variant = "standard",
  className,
}) => {
  const [isVisible, setIsVisible] = useState(false),
    [isDismissed, setIsDismissed] = useState(false),
    [showTextDialog, setShowTextDialog] = useState(false),
    [formState, setFormState] = useState<"idle" | "submitting" | "success" | "error">("idle"),
    [formData, setFormData] = useState({ message: "", name: "", phone: "" });

  useEffect(() => {
    if (typeof window !== "undefined" && dismissible) {
      const dismissed = sessionStorage.getItem(dismissKey) === "true";
      setIsDismissed(dismissed);
    }
  }, [dismissKey, dismissible]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const handleScroll = () => {
      setIsVisible(window.scrollY > scrollThreshold);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [scrollThreshold]);

  // Auto-open SMS dialog when ?text=open is in the URL
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    if (params.has("sms") && showTextUs !== "no") {
      setShowTextDialog(true);
    }
  }, [showTextUs]);

  const handleDismiss = useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDismissed(true);
        if (typeof window !== "undefined") {
          sessionStorage.setItem(dismissKey, "true");
        }
      },
      [dismissKey],
    ),

    handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    },
    handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const formEl = e.currentTarget;
      if (!formEl.checkValidity()) {
        formEl.reportValidity();
        return;
      }

      setFormState("submitting");

      try {
        const config = getWindowJobsiteConfig();
        if (config?.apiUrl && config?.siteId) {
          const params = new URLSearchParams(window.location.search),
            isPreview = params.get("preview") === "true",
            sessionId = isPreview ? null : sessionStorage.getItem("sp_session_id") || null,

            result = await submitLead({
              apiUrl: config.apiUrl,
              backupApiUrl: config.backupApiUrl,
              businessName: config.business?.name,
              payload: {
                ...formData,
                channel: "text",
                formType: "quick-quote",
                sessionId,
              },
              siteId: config.siteId,
            });
          if (result.status === "failed") {
            throw new Error("Lead submission failed");
          }
        }

        setFormState("success");
        setFormData({ message: "", name: "", phone: "" });
        window.setTimeout(() => {
          setFormState("idle");
          setShowTextDialog(false);
        }, 3000);
      } catch {
        setFormState("error");
        window.setTimeout(() => {
          setFormState("idle");
        }, 3000);
      }
    },
    telLink = phoneHref(phone),
    phoneDisplay = formatPhone(phone),
    isBold = variant === "bold";

  if (!phone || isDismissed || !isVisible) {
    return null;
  }

  return (
    <>
      {/* Sticky CTA Bar */}
      <div
        className={cn(
          "jobsite:fixed jobsite:bottom-0 jobsite:left-0 jobsite:right-0 jobsite:z-50",
          "jobsite:px-3 jobsite:pt-3 jobsite:pb-[calc(env(safe-area-inset-bottom)+0.75rem)] jobsite:md:hidden",
          "jobsite:animate-in jobsite:slide-in-from-bottom jobsite:duration-300",
          className,
        )}
      >
        <div className="jobsite:mx-auto jobsite:max-w-md">
          <div className="jobsite:flex jobsite:items-stretch jobsite:gap-1.5">
            {/* Text Us button */}
            {showTextUs !== "no" && (
              <button
                type="button"
                onClick={() => {
                  setShowTextDialog(true);
                }}
                data-track="text"
                data-track-label="sticky-bar"
                className={cn(
                  "jobsite:flex jobsite:min-w-0 jobsite:flex-1 jobsite:items-center jobsite:justify-center jobsite:gap-2.5",
                  "jobsite:bg-primary jobsite:text-primary-foreground",
                  "jobsite:px-4 jobsite:py-3.5",
                  "jobsite:shadow-[0_-6px_24px_rgba(0,0,0,0.2)]",
                  "jobsite:transition-all jobsite:active:translate-y-px",
                  "jobsite:text-sm",
                  // An edge of its own, so it still reads over the page's dark bands.
                  isBold && "jobsite:border-2 jobsite:border-primary-foreground/30",
                )}
                data-slot="button"
                style={{ borderRadius: "var(--jobsite-radius)" }}
              >
                <MessageSquare className="jobsite:h-5 jobsite:w-5" />
                <span>{isBold ? "Text us" : "Text Us"}</span>
              </button>
            )}

            {/* Call Now button — secondary (outline) when it sits next to Text
                Us, but promoted to the brand-filled primary when it's the only
                CTA, so it doesn't read as low-priority. */}
            <a
              data-slot="button"
              href={telLink}
              aria-label={`Call ${phoneDisplay}`}
              data-track-label="sticky-bar"
              className={cn(
                "jobsite:flex jobsite:min-w-0 jobsite:flex-1 jobsite:items-center jobsite:justify-center jobsite:gap-2.5",
                showTextUs === "no"
                  ? "jobsite:bg-primary jobsite:text-primary-foreground jobsite:shadow-[0_-6px_24px_rgba(0,0,0,0.2)]"
                  : "jobsite:border jobsite:border-border/70 jobsite:bg-background/98 jobsite:text-foreground jobsite:shadow-[0_-6px_24px_rgba(0,0,0,0.12)] jobsite:backdrop-blur-md",
                "jobsite:px-4 jobsite:py-3.5",
                "jobsite:transition-all jobsite:active:translate-y-px",
                "jobsite:text-sm",
                // Bold: calling is the one thing to press, so it takes the accent, beside Text us or alone.
                isBold && "jobsite:border-0 jobsite:bg-accent jobsite:text-accent-foreground jobsite:shadow-[0_-6px_24px_rgba(0,0,0,0.2)] jobsite:backdrop-blur-none",
              )}
              style={{ borderRadius: "var(--jobsite-radius)" }}
            >
              <Phone className="jobsite:h-5 jobsite:w-5" />
              <span>{buttonText}</span>
            </a>

            {/* Dismiss */}
            {dismissible && (
              <button
                onClick={handleDismiss}
                className={cn(
                  "jobsite:flex jobsite:w-10 jobsite:shrink-0 jobsite:items-center jobsite:justify-center",
                  "jobsite:border jobsite:border-border/70 jobsite:bg-background/98 jobsite:text-muted-foreground",
                  "jobsite:shadow-[0_-6px_24px_rgba(0,0,0,0.08)] jobsite:backdrop-blur-md",
                  "jobsite:transition-colors jobsite:hover:text-foreground",
                  isBold && "jobsite:w-11 jobsite:border-2 jobsite:border-primary-foreground/30 jobsite:bg-primary jobsite:text-primary-foreground/80 jobsite:hover:text-primary-foreground",
                )}
                style={{ borderRadius: "var(--jobsite-radius)" }}
                aria-label="Dismiss"
              >
                <X className="jobsite:h-4 jobsite:w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Text Us Dialog */}
      <Dialog open={showTextDialog} onOpenChange={setShowTextDialog}>
        <DialogContent className="jobsite:sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="jobsite:font-heading jobsite:text-2xl">
              Send Us a Text
            </DialogTitle>
            <DialogDescription>We usually respond via text within a few minutes.</DialogDescription>
          </DialogHeader>

          {formState === "success" ? (
            <div className="jobsite:flex jobsite:flex-col jobsite:items-center jobsite:gap-3 jobsite:py-6 jobsite:text-center">
              <CheckCircle2 className="jobsite:h-12 jobsite:w-12 jobsite:text-emerald-500" />
              <div>
                <p className="jobsite:text-lg jobsite:font-semibold jobsite:text-foreground">
                  Message sent!
                </p>
                <p className="jobsite:text-sm jobsite:text-muted-foreground jobsite:mt-1">
                  We'll text you back shortly.
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} autoComplete="off" className="jobsite:space-y-4">
              <div className="jobsite:grid jobsite:gap-4 jobsite:sm:grid-cols-2">
                <Input
                  type="text"
                  name="name"
                  autoComplete="off"
                  placeholder="Your Name *"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  disabled={formState === "submitting"}
                  className="jobsite:h-12 jobsite:text-base"
                />
                <PhoneInput
                  name="phone"
                  autoComplete="off"
                  placeholder="Phone Number *"
                  required
                  value={formData.phone}
                  onValueChange={(typed) => {
                    setFormData((prev) => ({ ...prev, phone: typed }));
                  }}
                  disabled={formState === "submitting"}
                  className="jobsite:h-12 jobsite:text-base"
                />
              </div>

              <Textarea
                name="message"
                autoComplete="off"
                placeholder="How can we help? (optional)"
                rows={3}
                value={formData.message}
                onChange={handleChange}
                disabled={formState === "submitting"}
                className="jobsite:resize-none jobsite:text-base"
              />

              <Button
                type="submit"
                disabled={formState === "submitting"}
                className="jobsite:h-12 jobsite:w-full jobsite:font-semibold jobsite:text-base"
                size="lg"
              >
                {formState === "submitting" ? "Sending..." : "Send"}
              </Button>

              {formState === "error" && (
                <div className="jobsite:flex jobsite:items-center jobsite:gap-2 jobsite:text-sm jobsite:text-destructive">
                  <XCircle className="jobsite:h-4 jobsite:w-4 jobsite:shrink-0" />
                  <span>Something went wrong. Please try again.</span>
                </div>
              )}

              <p className="jobsite:text-[11px] jobsite:leading-relaxed jobsite:text-muted-foreground">
                By providing your phone number, you consent to receive calls and text messages
                (SMS). You can unsubscribe at any time by replying STOP. Message and data rates may
                apply.
              </p>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export type { MobileStickyCallProps };
export { MobileStickyCall };
